import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from '../components/Button';
import { cn } from '../lib/cn';

const STORAGE_KEY = 'possignin_cookie_consent_v1';

type Choice = 'all' | 'essential' | null;

/**
 * GDPR-style cookie consent banner. Slides up from bottom-right on desktop,
 * full-width strip on mobile. Persists choice in localStorage.
 *
 * Note: localStorage usage here is for UI prefs only — never auth tokens.
 */
export function CookieConsent() {
  const [choice, setChoice] = useState<Choice>('all'); // pessimistic default — hide until decided
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY) as Choice;
      if (!stored) {
        setOpen(true);
        setChoice(null);
      } else {
        setChoice(stored);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  function persist(c: Choice) {
    setChoice(c);
    setOpen(false);
    try {
      if (c) window.localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // ignore
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 px-4 pb-4',
        'sm:inset-x-auto sm:right-4 sm:max-w-sm',
        'pointer-events-none',
      )}
    >
      <div
        className={cn(
          'pointer-events-auto rounded-xl border bg-white dark:bg-zinc-900 shadow-lg',
          'border-zinc-200 dark:border-zinc-700',
          'p-4 sm:p-5',
        )}
      >
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 grid place-items-center rounded-md bg-brand-subtle dark:bg-brand-dm-subtle/40 flex-shrink-0">
            <Cookie className="w-4 h-4 text-brand dark:text-brand-dm" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
              We use cookies to keep things running
            </p>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
              Essential cookies remember your session. Optional cookies help us understand what
              works (and what doesn't) so we can build a better POS.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close cookie banner"
            onClick={() => persist('essential')}
            className="w-7 h-7 grid place-items-center rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => persist('all')}>
            Accept all
          </Button>
          <Button variant="secondary" size="sm" onClick={() => persist('essential')}>
            Essential only
          </Button>
          <a
            href="https://itcitysoft.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="ml-auto self-center text-xs font-medium text-brand dark:text-brand-dm hover:underline"
          >
            Privacy policy
          </a>
        </div>
      </div>
    </div>
  );
}
